import { useState, useEffect, useRef } from "react";

export default function Hero() {
  const [counters, setCounters] = useState({
    students: 0,
    awards: 0,
    experience: 0,
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const animateCounter = (
      target: number,
      key: keyof typeof counters,
      duration: number = 2500
    ) => {
      const startTime = Date.now();
      const startValue = 0;

      const updateCounter = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function for smooth animation
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.floor(
          startValue + (target - startValue) * easeOutCubic
        );

        setCounters((prev) => ({
          ...prev,
          [key]: currentValue,
        }));

        if (progress < 1) {
          requestAnimationFrame(updateCounter);
        }
      };

      requestAnimationFrame(updateCounter);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);

            // Start animations with delays for better effect
            setTimeout(() => animateCounter(500, "students", 2000), 800);
            setTimeout(() => animateCounter(50, "awards", 2200), 1000);
            setTimeout(() => animateCounter(15, "experience", 2400), 1200);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, [hasAnimated]);

  return (
    <section
      id="home"
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-50 via-blue-50 to-green-50"
      style={{
        backgroundImage: `url('/background.jpg')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-blue-900/70 via-green-900/60 to-yellow-900/50"></div>

      <div className="relative z-10 container mx-auto px-4 py-20">
        <div className="w-full max-w-4xl mx-auto text-center text-white">
          <h1 className="mb-6 leading-tight">
            <span className="block text-7xl md:text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-red-400 to-green-400">
              OLYMPIC
            </span>
            <span className="block text-3xl md:text-5xl font-semibold text-white/90 mt-2">
              ACADEMY
            </span>
          </h1>

          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Nơi khai phóng tiềm năng và dẫn dắt thế hệ tương lai Việt Nam trong
            kỷ nguyên số. Đào tạo chuyên sâu Toán học, Lập trình và Trí tuệ Nhân
            tạo (AI) với tinh thần Olympic: Xuất sắc - Tôn trọng - Tình bạn
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <button
              onClick={() => scrollToSection("programs")}
              className="bg-gradient-to-r from-blue-500 via-green-500 to-yellow-500 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-blue-600 hover:via-green-600 hover:to-yellow-600 transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Khám phá chương trình
            </button>
            <button
              onClick={() => scrollToSection("contact")}
              className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-gradient-to-r hover:from-yellow-400 hover:to-red-500 hover:border-transparent transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Tham gia Olympic Academy
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center transform transition-all duration-500 hover:scale-105">
              <div className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-red-400 mb-2 font-mono">
                {counters.students}+
              </div>
              <div className="text-gray-200 text-lg">Tài năng trẻ xuất sắc</div>
            </div>
            <div className="text-center transform transition-all duration-500 hover:scale-105">
              <div className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-green-400 mb-2 font-mono">
                {counters.awards}+
              </div>
              <div className="text-gray-200 text-lg">Giải thưởng quốc tế</div>
            </div>
            <div className="text-center transform transition-all duration-500 hover:scale-105">
              <div className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-yellow-400 mb-2 font-mono">
                {counters.experience}+
              </div>
              <div className="text-gray-200 text-lg">Năm kinh nghiệm</div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <button
          onClick={() => scrollToSection("about")}
          className="w-8 h-8 flex items-center justify-center text-white cursor-pointer"
        >
          <i className="ri-arrow-down-line text-2xl"></i>
        </button>
      </div>
    </section>
  );
}
